/**
 * One collapsible module section of the notification setup grid — Purchasing,
 * Quality, Documents & Compliance, Onboarding, RFQ, System.
 *
 * Sections come from the catalogue endpoint, so a new domain (or a new event in
 * an existing one) appears here the next time an admin opens the tab, with no
 * hand-maintained list to update.
 *
 * The bulk buttons act on `events` — the rows this section is rendering right
 * now — and never on the domain behind them. With a search active those are not
 * the same set, and a bulk action that reached past the filter changed rows the
 * admin never saw, staged them silently and saved them. So the buttons say how
 * many rows they will touch whenever the header count says the list is narrowed.
 *
 * Retired events are listed but never counted or touched: "X of Y on" counts
 * live rows only, the bulk buttons skip retired ones, and a section with no
 * live row at all says "All retired" and offers no bulk action.
 */
import type {
  NotificationChannel,
  NotificationTenantDefaultFrequency,
} from '../../../../lib/api';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../../ui/collapsible';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../ui/table';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { NotificationEventRow } from './NotificationEventRow';
import { canTurnOn, isEventOn } from './eventState';
import { CHANNEL_LABELS, SETUP_CHANNELS, type EventConfig, type SetupChoice, type SetupEvent } from './types';

interface NotificationDomainSectionProps {
  label: string;
  /**
   * Events after the tab's search filter — may be empty. This is the exact set
   * the section renders, and the exact set its bulk buttons act on.
   */
  events: SetupEvent[];
  /** Events in this domain before filtering, for the header count. */
  totalCount: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canConfigure: boolean;
  frequencyChoices: SetupChoice[];
  /**
   * Tenant-wide cadence every row inherits unless it has one of its own. `''`
   * sends each row to its own platform default instead.
   */
  defaultFrequency: NotificationTenantDefaultFrequency;
  emailsEnabled: boolean;
  configFor: (event: SetupEvent) => EventConfig;
  isDirty: (event: SetupEvent) => boolean;
  onUpdate: (event: SetupEvent, patch: Partial<EventConfig>) => void;
  onToggleChannel: (event: SetupEvent, channel: NotificationChannel, on: boolean) => void;
  onReset: (event: SetupEvent) => void;
  /** Stages one enabled state across exactly the rows it is handed. */
  onSetEventsEnabled: (events: SetupEvent[], enabled: boolean) => void;
}

export function NotificationDomainSection({
  label,
  events,
  totalCount,
  open,
  onOpenChange,
  canConfigure,
  frequencyChoices,
  defaultFrequency,
  emailsEnabled,
  configFor,
  isDirty,
  onUpdate,
  onToggleChannel,
  onReset,
  onSetEventsEnabled,
}: NotificationDomainSectionProps) {
  // Retired rows are shown but never counted: they are never sent, and no
  // bulk action can change them, so they belong to neither side of "X of Y".
  const liveEvents = events.filter(canTurnOn);
  const retiredShown = events.length - liveEvents.length;
  const onCount = liveEvents.filter((event) =>
    isEventOn(event, configFor(event), defaultFrequency),
  ).length;
  const allRetired = events.length > 0 && liveEvents.length === 0;
  const dirtyCount = events.filter(isDirty).length;
  // A search narrows `events` but not `totalCount`, so say so — the header read
  // "(3 events) ... 1 of 1 on" otherwise, which looks like a counting bug.
  const filtered = events.length !== totalCount;
  const hidden = totalCount - events.length;

  /**
   * "Enable all" only while the section really is showing all of itself. Once a
   * search has narrowed it, the button counts what it will change — live rows
   * only, since retired ones are skipped — so it can never read as a promise
   * about rows scrolled out of sight.
   */
  const bulkLabel = (verb: string): string =>
    filtered ? `${verb} ${liveEvents.length} shown` : `${verb} all`;

  const retiredNote = retiredShown > 0 ? ' Retired events are skipped.' : '';

  const bulkTitle = (verb: string): string | undefined =>
    filtered
      ? `${verb}s the ${liveEvents.length} event${liveEvents.length === 1 ? '' : 's'} shown here. `
        + `The ${hidden} hidden by your search ${hidden === 1 ? 'is' : 'are'} left as ${hidden === 1 ? 'it is' : 'they are'}.`
        + retiredNote
      : retiredNote.trim() || undefined;

  return (
    <Collapsible open={open} onOpenChange={onOpenChange} className="rounded-lg border">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
        <CollapsibleTrigger asChild>
          <button type="button" className="flex items-center gap-2 text-left">
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <span className="font-semibold">{label}</span>
            <span className="text-sm text-muted-foreground">
              {filtered
                ? `(${events.length} of ${totalCount} events)`
                : `(${totalCount} event${totalCount === 1 ? '' : 's'})`}
            </span>
            {dirtyCount > 0 && <Badge variant="secondary">{dirtyCount} unsaved</Badge>}
          </button>
        </CollapsibleTrigger>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {allRetired
              ? `All ${filtered ? 'shown ' : ''}retired`
              : `${onCount} of ${liveEvents.length} ${filtered ? 'shown ' : ''}on`}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={!canConfigure || liveEvents.length === 0}
            title={bulkTitle('Enable')}
            onClick={() => onSetEventsEnabled(liveEvents, true)}
          >
            {bulkLabel('Enable')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!canConfigure || liveEvents.length === 0}
            title={bulkTitle('Disable')}
            onClick={() => onSetEventsEnabled(liveEvents, false)}
          >
            {bulkLabel('Disable')}
          </Button>
        </div>
      </div>

      <CollapsibleContent>
        {events.length === 0 ? (
          <p className="px-4 pb-4 text-sm text-muted-foreground">
            No notifications in {label} match your search.
          </p>
        ) : (
          <div className="overflow-x-auto border-t">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[280px]">Event</TableHead>
                  <TableHead className="w-[70px] text-center">On</TableHead>
                  {SETUP_CHANNELS.map((channel) => (
                    <TableHead key={channel} className="w-[80px] text-center">
                      {CHANNEL_LABELS[channel]}
                    </TableHead>
                  ))}
                  <TableHead className="w-[210px]">Frequency</TableHead>
                  <TableHead className="w-[60px] text-right">Reset</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((event) => (
                  <NotificationEventRow
                    key={event.key}
                    event={event}
                    config={configFor(event)}
                    dirty={isDirty(event)}
                    canConfigure={canConfigure}
                    frequencyChoices={frequencyChoices}
                    defaultFrequency={defaultFrequency}
                    emailsEnabled={emailsEnabled}
                    onUpdate={onUpdate}
                    onToggleChannel={onToggleChannel}
                    onReset={onReset}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
